import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
    uploadUsersCSV,
    fetchUsers,
    getParticipantsCSVTemplate,
} from '../../../features/users/usersSlice'

import './UploadUsersForm.css'
import CSVImage from '../../../assets/images/csv_icon.svg'
import CButton from '../../../components/UI/CButton'
import CDragAndDrop from '../../../components/UI/CDragAndDrop'

/**
 * Reusable component for uploading users via CSV
 * Handles all upload logic internally
 */
const UploadUsersForm = ({
    onClose,
    onSuccess,
    onError,
}) => {
    const dispatch = useDispatch()
    const { loading } = useSelector((state) => state.users)

    const [csvFile, setCsvFile] = useState({
        fileData: null,
        fileName: ''
    })

    const onFileUpload = async (files) => {
        if (files.length > 0) {
            const file = files[0];
            setCsvFile({ fileData: file, fileName: file.name });
        }
    };

    const onFileRemove = (files) => {
        setCsvFile({ fileData: null, fileName: '' });
    }

    const handleCancel = () => {
        setCsvFile({ fileData: null, fileName: '' });
        if (onClose) onClose()
    }

    const handleSubmit = async () => {
        try {
            const formData = new FormData();
            formData.append('filecontents', csvFile.fileData);

            const result = await dispatch(uploadUsersCSV(formData)).unwrap()

            // Refresh users list
            dispatch(fetchUsers())

            // Reset form
            setCsvFile({ fileData: null, fileName: '' });

            if (onSuccess) onSuccess(result)
            if (onClose) onClose()
        } catch (err) {
            if (onError) onError(err)
        }
    }

    const handleDownloadTemplate = () => {
        dispatch(getParticipantsCSVTemplate()).unwrap().then((response) => {
            const link = document.createElement('a');
            link.href = response.url;
            link.download = 'participants_upload_template.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }).catch((err) => {
            console.error('Error downloading template:', err);
        })
    }

    return (
        <div className="upload-users-form">
            <p className="form-description">
                Upload a CSV file to add or update multiple users at once.
                The CSV should include columns: surname, firstnames, idnumber, cell, email.
            </p>

            <CDragAndDrop
                showPreview={false}
                showFileNames
                onFileAdd={(val) => onFileUpload(val)}
                onFileRemove={(val) => onFileRemove(val)}
                maxFiles={1}
                acceptedFiles={{ ['text/csv']: [] }}
            />

            <div className="template-container">
                <img src={CSVImage} alt="CSV Template" className="csv-template-icon" />
                <div className="template-info-text">
                    <span className="header-5 title">Sample CSV Template</span>
                    <span className="text-6 description">Start with the attached template and customise as needed.</span>
                </div>
                <CButton
                    label="Download"
                    onClick={handleDownloadTemplate}
                    variant="outlined"
                    color="primary"
                    loading={loading.csvTemplate}
                />
            </div>

            <div className="form-actions">
                <CButton
                    label="Cancel"
                    onClick={handleCancel}
                    variant="outlined"
                    color="secondary"
                    disabled={loading.uploadUsers}
                />
                <CButton
                    label="Upload"
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    loading={loading.uploadUsers}
                />
            </div>
        </div>
    )
}

export default UploadUsersForm
